import moment from 'moment-timezone';
import { read_api_error } from '../../../../../utils/api_error_message';
import { trim_reason } from '../../../../util_components/required_reason_textarea/reason_rules';
import {
  KIND_CANCEL,
  ist_input_to_iso,
  is_one_on_one
} from '../../../../util_components/care_request/care_request_rules';
import {
  is_pay_time_warning,
  read_care_save_warning,
  with_care_ack
} from '../../../../util_components/care_request/care_ack_rules';
import { build_pay_time_ack } from '../../../../util_components/pay_time_warning/pay_time_warning_rules';

const CLOCK_SKEW_MS = 2 * 60 * 1000;

// shared by the three pause cancel options; Cancelled by Student also sets the admin flag
export const build_pause_cancel_payload = (base, cancelState) => {
  const payload = {
    ...base,
    cancel_reason: trim_reason(cancelState.cancel_reason)
  };
  if (cancelState.status === 'CANCELLED') {
    payload.cancelled_by_admin = 1;
  }
  // explicit 0 when unticked: apis then saves "cancel all future" as admin-only, uncharged
  payload.cancelled_by_student = 0;
  if (cancelState.cancelled_by_student) {
    payload.cancelled_by_student = 1;
    payload.cancelled_by_admin = 1;
    // apis measures the 12h late-cancel window from when the student asked (PROD-7828)
    const requested = ist_input_to_iso(cancelState.student_requested_at);
    if (requested) payload.student_requested_at = requested;
  }
  return payload;
};

// '' = valid. Required with Cancelled by Student: it decides the member's charge and the teacher's pay.
// Not asked for a group class: apis never charges it, so its request time is unused.
export const student_requested_at_error = (cancelState, nowMs, sessionType) => {
  if (!cancelState.cancelled_by_student || sessionType === 'GROUP_SESSION_STUDENT') return '';
  const iso = ist_input_to_iso(cancelState.student_requested_at);
  if (!iso) return 'Enter when the student asked to cancel (IST).';
  if (moment(iso).valueOf() > nowMs + CLOCK_SKEW_MS)
    return "Student requested at can't be in the future.";
  return '';
};

// Who asked is required (PROD-7828 A-52): Student or Admin (the team); a pause is never the teacher's.
export const pause_who_missing = (cancelState) =>
  cancelState.status === 'CANCELLED' &&
  !cancelState.cancelled_by_student &&
  !cancelState.cancelled_by_admin;

// The cancelled classes apis reports back, else the one class this option cancelled.
export const cancelled_session_uuids = (response, fallbackUuid) => {
  const uuids = response?.data?.cancelled_session_uuids;
  if (Array.isArray(uuids)) return uuids;
  return fallbackUuid ? [fallbackUuid] : [];
};

// A partial repeat/pause cancel is still a 400, but apis lists both sides under `reason`; null when it isn't one.
export const partial_cancel_outcome = (error) => {
  const reason = error?.response?.data?.reason;
  if (!reason || !Array.isArray(reason.cancelled_session_uuids)) return null;
  return {
    cancelled_session_uuids: reason.cancelled_session_uuids,
    failed_session_uuids: Array.isArray(reason.failed_session_uuids)
      ? reason.failed_session_uuids
      : []
  };
};

export const partial_cancel_message = (outcome, detail) => {
  const head = `${outcome.cancelled_session_uuids.length} class(es) cancelled, ${outcome.failed_session_uuids.length} could not be cancelled.`;
  return detail ? `${head} ${detail}` : head;
};

// One Care CANCEL record per cancelled 1-on-1, so teacher pay measures from the student's request too.
export const build_pause_care_records = ({ sessionUuids, sessionType, cancelState, makeUuid }) => {
  if (!cancelState.cancelled_by_student || !is_one_on_one(sessionType)) return [];
  const requestedAt = ist_input_to_iso(cancelState.student_requested_at);
  if (!requestedAt) return [];
  return sessionUuids.map((sessionUuid) => ({
    uuid: makeUuid(),
    session_uuid: sessionUuid,
    change_kind: KIND_CANCEL,
    initiated_by: 'STUDENT',
    requested_at: requestedAt,
    note: trim_reason(cancelState.cancel_reason)
  }));
};

// Why a Care record failed, in plain words: the server's message, else its error code.
export const care_failure_reason = (e) => {
  const { message, code } = read_api_error(e);
  return message || code || '';
};

// Saves each record on its own so one failure never blocks the rest. Returns the failures as
// [{session_uuid, reason, record, pay_warning}]; pay_warning is goserver's 409 PAY_TIME_WARNING, else null.
export const save_care_records = async (records, save) => {
  const failed = [];
  for (const record of records) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await save(record);
    } catch (e) {
      const w = read_care_save_warning(e);
      failed.push({
        session_uuid: record.session_uuid,
        reason: care_failure_reason(e),
        record,
        pay_warning: is_pay_time_warning(w) ? w : null
      });
    }
  }
  return failed;
};

// A-52: the pay-time failures get one acknowledgement step (retry + the step's warning); the rest
// keep their on-screen reason (other). Facts are per class, so the step shows them for one class only.
export const plan_care_retry = (failed) => {
  const retry = failed.filter((f) => f.pay_warning);
  const other = failed.filter((f) => !f.pay_warning);
  if (!retry.length) return { retry, other, warning: null };
  const one = retry.length === 1 ? retry[0].pay_warning : null;
  return {
    retry,
    other,
    warning: {
      warnings: [...new Set(retry.flatMap((f) => f.pay_warning.warnings))],
      facts: one ? one.facts : {},
      message: one ? one.message : ''
    }
  };
};

// Page state after a batch save: listed failures grow by `other`; retry is the pending step or null.
export const after_care_save = (listed, failed) => {
  const plan = plan_care_retry(failed);
  return { listed: [...listed, ...plan.other], retry: plan.retry.length ? plan : null };
};

// Each record resent with its own 409's warnings and the one shared note; a request_time_ack it had stays.
export const care_retry_payloads = (retry, note) =>
  retry.map((f) => with_care_ack(f.record, build_pay_time_ack(f.pay_warning.warnings, note)));

export const care_records_warning = (failed) =>
  failed.length
    ? `Cancelled, but the request time was not saved for ${failed.length} class(es). Add it under Change requests on: ${failed
        .map((f) => (f.reason ? `${f.session_uuid} (${f.reason})` : f.session_uuid))
        .join('; ')}`
    : '';
